import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail as fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { HOLIDAY_TYPES, dayLabel, todayIso } from "@/lib/hr";

const toIso = (d) => d.toLocaleDateString("en-CA");
const fromIso = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };

function AddHoliday({ onAdded, initialDate }) {
  const [date, setDate] = useState(initialDate || todayIso());
  const [name, setName] = useState("");
  const [type, setType] = useState("public");
  useEffect(() => { if (initialDate) setDate(initialDate); }, [initialDate]);
  const add = async () => {
    if (!name.trim()) { toast.error("Enter a holiday name"); return; }
    try { await api.post("/hr/holidays", { date, name, type }); toast.success("Holiday added"); setName(""); onAdded(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap gap-2 items-end" data-testid="add-holiday-form">
      <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40 h-9" data-testid="holiday-date-input" />
      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Holiday name, e.g. Diwali" className="flex-1 min-w-[180px] h-9" data-testid="holiday-name-input" />
      <Select value={type} onValueChange={setType}>
        <SelectTrigger className="w-32 h-9" data-testid="holiday-type-select"><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(HOLIDAY_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
      </Select>
      <Button onClick={add} className="h-9 bg-indigo-600 hover:bg-indigo-700" data-testid="holiday-add-button"><Plus className="w-4 h-4 mr-1" />Add</Button>
    </div>
  );
}

export default function HolidaysTab({ canManage }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [list, setList] = useState([]);
  const [month, setMonth] = useState(new Date());
  const [picked, setPicked] = useState(null);
  const load = useCallback(() => api.get("/hr/holidays", { params: { year } }).then((r) => setList(r.data)), [year]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (month.getFullYear() !== year) setYear(month.getFullYear()); }, [month, year]);

  const remove = async (h) => {
    if (!window.confirm(`Delete ${h.name}?`)) return;
    await api.delete(`/hr/holidays/${h.id}`); toast.success("Holiday deleted"); load();
  };
  const modifiers = Object.fromEntries(Object.keys(HOLIDAY_TYPES).map((t) => [t, list.filter((h) => h.type === t).map((h) => fromIso(h.date))]));
  const today = todayIso();

  return (
    <div className="space-y-4" data-testid="holidays-tab">
      {canManage && <AddHoliday onAdded={load} initialDate={picked} />}
      <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-4 items-start">
        <div className="bg-white border border-slate-200 rounded-xl p-3 w-fit mx-auto lg:mx-0" data-testid="holiday-calendar">
          <Calendar mode="single" month={month} onMonthChange={setMonth} selected={picked ? fromIso(picked) : undefined}
            onSelect={(d) => d && setPicked(toIso(d))} modifiers={modifiers}
            modifiersClassNames={{ public: "!bg-indigo-100 !text-indigo-800 font-semibold", optional: "!bg-amber-100 !text-amber-800 font-semibold", company: "!bg-emerald-100 !text-emerald-800 font-semibold" }} />
          <div className="flex gap-3 justify-center pt-2 border-t border-slate-100 mt-1">
            {Object.values(HOLIDAY_TYPES).map((t) => <span key={t.label} className="text-[11px] text-slate-500 inline-flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${t.dot}`} />{t.label}</span>)}
          </div>
        </div>
        <div className="space-y-2" data-testid="holiday-list">
          <p className="text-sm font-semibold text-slate-700">{year} holidays ({list.length})</p>
          {list.map((h) => (
            <div key={h.id} className={`bg-white border rounded-xl px-4 py-3 flex items-center gap-3 ${h.date < today ? "opacity-60 border-slate-200" : "border-slate-200"}`} data-testid={`holiday-row-${h.id}`}>
              <div className="w-12 text-center shrink-0"><p className="text-[10px] uppercase text-slate-400">{dayLabel(h.date, { month: "short" })}</p><p className="font-display text-xl font-bold text-slate-900 leading-none">{Number(h.date.slice(8))}</p></div>
              <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-900 truncate">{h.name}</p><p className="text-xs text-slate-500">{dayLabel(h.date, { weekday: "long" })}</p></div>
              <Badge variant="outline" className={`h-5 text-[10px] ${HOLIDAY_TYPES[h.type]?.chip}`}>{HOLIDAY_TYPES[h.type]?.label}</Badge>
              {canManage && <button onClick={() => remove(h)} className="text-slate-300 hover:text-red-500" data-testid={`holiday-delete-${h.id}`}><Trash2 className="w-4 h-4" /></button>}
            </div>
          ))}
          {!list.length && <p className="text-sm text-slate-400 py-6">No holidays added for {year}.</p>}
        </div>
      </div>
    </div>
  );
}
